import { $tenant, useTranslation } from '@msdk/common'
import {
  BadRequestIcon,
  ButtonPrimary,
  ForbiddenIcon,
  GeneralErrorIcon,
  InternalServerErrorIcon,
  NoContentIcon,
  NotFoundIcon,
  ServiceUnavailableIcon,
  styled,
  THEME_CONST,
  UnauthorizedIcon,
} from '@msdk/ui'
import React from 'react'
import { useNavigate } from 'react-router-dom'
import { useRecoilValue } from 'recoil'

const Wrapper = styled('div')`
  height: calc(100vh - ${THEME_CONST.navHeight}px - 58px);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`

const MessageContainer = styled('div')`
  max-width: 75vh;
  display: flex;
  flex-direction: column;
  align-items: center;
`

const MessageTitle = styled('p')`
  font-weight: 700;
  font-size: 24px;
  line-height: 32px;
  text-align: center;
  margin: 48px 0 16px;
`

const MessageSubtitle = styled('p')`
  font-weight: 400;
  font-size: 16px;
  line-height: 24px;
  margin: 0;
  text-align: center;
  color: #0009;
`

const MessageButton = styled(ButtonPrimary)`
  margin-top: 24px;
`

export type ErrorType =
  | 'NoContent'
  | 'BadRequest'
  | 'Unauthorized'
  | 'Forbidden'
  | 'NotFound'
  | 'InternalServerError'
  | 'ServiceUnavailable'
  | 'GeneralError'

interface Props {
  type: ErrorType
}

interface Mapping {
  icon: React.ReactNode
  title: string
  firstSubTitle: string
  secondSubtitle?: string
  buttonDescription?: string
}

export function ApplicationError(props: Props) {
  const t = useTranslation()
  const tenant = useRecoilValue($tenant)
  const navigate = useNavigate()

  const ERROR_MAPPING: Record<ErrorType, Mapping> = {
    NoContent: {
      icon: <NoContentIcon />,
      title: t('CMPSR_NO_CONTENT_TITLE', 'No content to display'),
      firstSubTitle: t('CMPSR_NO_CONTENT_SUBTITLE', 'Thre is no content in this page.'),
      buttonDescription: t('CMPSR_APPLICATION_ERROR_BUTTON_HOME', 'Go to Homepage'),
    },
    Unauthorized: {
      icon: <UnauthorizedIcon />,
      title: t('CMPSR_UNAUTHORIZED_TITLE', 'Access denied'),
      firstSubTitle: t('CMPSR_UNAUTHORIZED_SUBTITLE1', 'You do not have permissions to view this page.'),
      secondSubtitle: t('CMPSR_UNAUTHORIZED_SUBTITLE_2', 'Check your credentials.'),
      buttonDescription: t('CMPSR_APPLICATION_ERROR_BUTTON_HOME', 'Go to Homepage'),
    },
    Forbidden: {
      icon: <ForbiddenIcon />,
      title: t('CMPSR_FORBIDDEN_TITLE', 'Access restricted'),
      firstSubTitle: t('CMPSR_FORBIDDEN_SUBTITLE', 'We are sorry, but you have no access to this page.'),
      buttonDescription: t('CMPSR_APPLICATION_ERROR_BUTTON_HOME', 'Go to Homepage'),
    },
    NotFound: {
      icon: <NotFoundIcon />,
      title: t('CMPSR_NOT_FOUND_TITLE', 'Page not found'),
      firstSubTitle: t('CMPSR_NOT_FOUND_SUBTITLE1', 'The page you are looking for does not exist.'),
      secondSubtitle: t('CMPSR_NOT_FOUND_SUBTITLE_2', 'Check the URL.'),
      buttonDescription: t('CMPSR_APPLICATION_ERROR_BUTTON_HOME', 'Go to Homepage'),
    },
    InternalServerError: {
      icon: <InternalServerErrorIcon />,
      title: t('CMPSR_INTERNAL_SERVER_ERROR_TITLE', 'Internal server error'),
      firstSubTitle: t('CMPSR_INTERNAL_SERVER_ERROR_SUBTITLE', 'We are sorry, but unexpected error has occurred.'),
      secondSubtitle: t('CMPSR_INTERNAL_SERVER_ERROR_SUBTITLE2', 'Try again later.'),
      buttonDescription: t('CMPSR_APPLICATION_ERROR_BUTTON_HOME', 'Go to Homepage'),
    },
    ServiceUnavailable: {
      icon: <ServiceUnavailableIcon />,
      title: t('CMPSR_SERVICE_UNAVAILABLE_TITLE', 'Service Unavailable'),
      firstSubTitle: t('CMPSR_SERVICE_UNAVAILABLE_SUBTITLE', 'We are sorry, but our server is not responding.'),
      secondSubtitle: t('CMPSR_SERVICE_UNAVAILABLE_SUBTITLE2', 'Try again later.'),
      buttonDescription: t('CMPSR_APPLICATION_ERROR_BUTTON_HOME', 'Go to Homepage'),
    },
    BadRequest: {
      icon: <BadRequestIcon />,
      title: t('CMPSR_BAD_REQUEST_TITLE', "This page isn't working at the moment"),
      firstSubTitle: t('CMPSR_BAD_REQUEST_SUBTITLE1', 'If the problem continues, check the input or try back later.'),
      buttonDescription: t('CMPSR_APPLICATION_ERROR_BUTTON_REFRESH', 'Refresh'),
    },
    GeneralError: {
      icon: <GeneralErrorIcon />,
      title: t('CMPSR_GENERAL_ERROR_TITLE', 'Oops!'),
      firstSubTitle: t('CMPSR_GENERAL_ERROR_SUBTITLE1', 'Something went wrong. Please try again later.'),
    },
  }

  const handleClick = () => {
    switch (props.type) {
      case 'NoContent':
      case 'Unauthorized':
      case 'Forbidden':
      case 'NotFound':
      case 'InternalServerError':
      case 'ServiceUnavailable':
        navigate(`/${tenant}`)
        break
      case 'BadRequest':
        window.location.reload()
        break
      default:
        throw new Error(`[ApplicationError] - unsupported button type ${props.type}`)
    }
  }

  return (
    <Wrapper>
      {ERROR_MAPPING[props.type].icon}
      <MessageContainer>
        <MessageTitle>{ERROR_MAPPING[props.type].title}</MessageTitle>
        <MessageSubtitle>{ERROR_MAPPING[props.type].firstSubTitle}</MessageSubtitle>
        {ERROR_MAPPING[props.type].secondSubtitle && (
          <MessageSubtitle>{ERROR_MAPPING[props.type].secondSubtitle}</MessageSubtitle>
        )}
        {ERROR_MAPPING[props.type].buttonDescription && (
          <MessageButton onClick={handleClick}>{ERROR_MAPPING[props.type].buttonDescription}</MessageButton>
        )}
      </MessageContainer>
    </Wrapper>
  )
}
